import type { DeviceStatus } from '../../types/DeviceListTypes';
import { isFrontDeviceId } from './devicePairing';
import { formatLifecycleSummary, isInServiceLifecycle } from './deviceLifecycle';
import type { RaceConfigurationRequirements, ValidationFieldCell, DeviceValidationBreakdown } from './types/DeviceValidationTypes';

export type { RaceConfigurationRequirements, ValidationFieldCell, DeviceValidationBreakdown };

export const DEVICE_STATUS_STALE_MS = 5 * 60 * 1000;
export const DEVICE_STATUS_STALE_REASON_PREFIX = 'Data stale';

/** Shown when device checks cannot run until race configuration (JSON) is saved for the race. */
export const RACE_CONFIGURATION_REQUIRED_FOR_VERIFICATION_MESSAGE =
  'Must upload race configuration file to run verification. Go back and upload it via Actions → Edit race configuration.';

export function isRaceConfigurationReadyForDeviceVerification(
  cfg: RaceConfigurationRequirements | null | undefined,
  loadOk = true,
): boolean {
  return (
    loadOk &&
    !!(cfg?.fw_version && String(cfg.fw_version).trim()) &&
    !!(cfg?.fw_version_front && String(cfg.fw_version_front).trim())
  );
}

/** Breakdown used when race configuration is missing — FW fails with the upload message; other columns are N/A. */
export function getDeviceValidationBreakdownWhenRaceConfigurationMissing(): DeviceValidationBreakdown {
  const na: ValidationFieldCell = { result: 'na', detail: '' };
  const configRequired: ValidationFieldCell = {
    result: 'fail',
    detail: RACE_CONFIGURATION_REQUIRED_FOR_VERIFICATION_MESSAGE,
  };
  return {
    freshness: na,
    firmware: configRequired,
    firmwareFront: configRequired,
    battery: na,
    debugLog: na,
    raceLog: na,
    lifecycle: na,
    notes: RACE_CONFIGURATION_REQUIRED_FOR_VERIFICATION_MESSAGE,
  };
}

export function toDeviceStatusSeenMs(status?: DeviceStatus): number {
  const updatedAt = status?.updatedAt;
  if (typeof updatedAt !== 'number' || !Number.isFinite(updatedAt) || updatedAt <= 0) return 0;
  return updatedAt > 1e12 ? updatedAt : updatedAt * 1000;
}

export function formatDeviceStatusLastSeen(status?: DeviceStatus): string {
  const lastSeenMs = toDeviceStatusSeenMs(status);
  if (lastSeenMs <= 0) return 'unknown';
  return new Date(lastSeenMs).toLocaleString();
}

export function isDeviceValidationStaleReason(reason: string): boolean {
  return String(reason ?? '').startsWith(DEVICE_STATUS_STALE_REASON_PREFIX);
}

/** Race configuration vs live status only (no lifecycle). */
export function getRaceConfigurationMismatchReasons(
  cfg: RaceConfigurationRequirements | null | undefined,
  status: DeviceStatus | undefined,
): string[] {
  const b = getDeviceValidationBreakdown(cfg, status, 'ACTIVE');
  const out: string[] = [];
  for (const c of [b.firmware, b.battery, b.debugLog, b.raceLog] as const) {
    if (c.result === 'fail' && c.detail) out.push(c.detail);
  }
  return out;
}

export function normalizeLifecycleStatus(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

export function getDeviceValidationBreakdown(
  cfg: RaceConfigurationRequirements | null | undefined,
  status: DeviceStatus | undefined,
  lifecycleStatusRaw?: unknown,
  nowMs: number = Date.now(),
  /** Paired front device's status, when known — used to check firmwareFront. */
  frontStatus?: DeviceStatus,
): DeviceValidationBreakdown {
  const lastSeenMs = toDeviceStatusSeenMs(status);
  const freshness: ValidationFieldCell =
    lastSeenMs > 0 && lastSeenMs >= nowMs - DEVICE_STATUS_STALE_MS
      ? { result: 'pass', detail: '' }
      : {
          result: 'fail',
          detail: `${DEVICE_STATUS_STALE_REASON_PREFIX}, last online at ${formatDeviceStatusLastSeen(status)}`,
        };

  const fw = typeof status?.fw === 'string' ? status.fw : '';
  const battMv =
    typeof status?.batt_mv === 'number' && Number.isFinite(status.batt_mv) ? status.batt_mv : undefined;
  const debugLogSize =
    typeof status?.debug_log_size === 'number' && Number.isFinite(status.debug_log_size)
      ? status.debug_log_size
      : undefined;
  const raceLogSize =
    typeof status?.race_log_size === 'number' && Number.isFinite(status.race_log_size)
      ? status.race_log_size
      : undefined;

  const reqFw = cfg && typeof cfg.fw_version === 'string' ? cfg.fw_version.trim() : '';
  let firmware: ValidationFieldCell;
  if (!reqFw) {
    firmware = { result: 'na', detail: 'No firmware requirement in race configuration' };
  } else if (fw !== reqFw) {
    firmware = {
      result: 'fail',
      detail: `FW expected ${reqFw}, got ${fw || 'unknown'}`,
    };
  } else {
    firmware = { result: 'pass', detail: '' };
  }

  const reqFwFront = cfg && typeof cfg.fw_version_front === 'string' ? cfg.fw_version_front.trim() : '';
  const fwFront = typeof frontStatus?.fw === 'string' ? frontStatus.fw : '';
  let firmwareFront: ValidationFieldCell;
  if (!reqFwFront) {
    firmwareFront = { result: 'na', detail: 'No front firmware requirement in race configuration' };
  } else if (!frontStatus) {
    firmwareFront = { result: 'na', detail: 'No paired front device to check' };
  } else if (fwFront !== reqFwFront) {
    firmwareFront = {
      result: 'fail',
      detail: `Front FW expected ${reqFwFront}, got ${fwFront || 'unknown'}`,
    };
  } else {
    firmwareFront = { result: 'pass', detail: '' };
  }

  let battery: ValidationFieldCell;
  if (
    !cfg ||
    typeof cfg.min_battery !== 'number' ||
    !Number.isFinite(cfg.min_battery) ||
    cfg.min_battery <= 0
  ) {
    battery = { result: 'na', detail: 'No minimum battery requirement' };
  } else if (typeof battMv !== 'number' || battMv < cfg.min_battery) {
    battery = {
      result: 'fail',
      detail: `Battery below minimum ${cfg.min_battery} mV (${typeof battMv === 'number' ? `${battMv} mV` : 'unknown'})`,
    };
  } else {
    battery = { result: 'pass', detail: '' };
  }

  let debugLog: ValidationFieldCell;
  if (
    !cfg ||
    typeof cfg.max_debug_size !== 'number' ||
    !Number.isFinite(cfg.max_debug_size) ||
    cfg.max_debug_size <= 0
  ) {
    debugLog = { result: 'na', detail: 'No debug log size limit' };
  } else if (
    typeof debugLogSize !== 'number' ||
    !Number.isFinite(debugLogSize) ||
    debugLogSize > cfg.max_debug_size
  ) {
    debugLog = {
      result: 'fail',
      detail:
        typeof debugLogSize === 'number' && Number.isFinite(debugLogSize)
          ? `Debug log size ${debugLogSize} B exceeds limit ${cfg.max_debug_size} B`
          : `Debug log size unknown; limit is ${cfg.max_debug_size} B`,
    };
  } else {
    debugLog = { result: 'pass', detail: '' };
  }

  let raceLog: ValidationFieldCell;
  if (
    !cfg ||
    typeof cfg.max_race_size !== 'number' ||
    !Number.isFinite(cfg.max_race_size) ||
    cfg.max_race_size <= 0
  ) {
    raceLog = { result: 'na', detail: 'No race log size limit' };
  } else if (
    typeof raceLogSize !== 'number' ||
    !Number.isFinite(raceLogSize) ||
    raceLogSize > cfg.max_race_size
  ) {
    raceLog = {
      result: 'fail',
      detail:
        typeof raceLogSize === 'number' && Number.isFinite(raceLogSize)
          ? `Race log size ${raceLogSize} B exceeds limit ${cfg.max_race_size} B`
          : `Race log size unknown; limit is ${cfg.max_race_size} B`,
    };
  } else {
    raceLog = { result: 'pass', detail: '' };
  }

  const lifecycleSummary = formatLifecycleSummary(lifecycleStatusRaw);
  const lifecycle: ValidationFieldCell = isInServiceLifecycle(lifecycleStatusRaw)
    ? { result: 'pass', detail: '' }
    : {
        result: 'fail',
        detail: `Device is not in service (${lifecycleSummary})`,
      };

  const failParts = [freshness, firmware, firmwareFront, battery, debugLog, raceLog, lifecycle]
    .filter((c) => c.result === 'fail' && c.detail)
    .map((c) => c.detail);
  const notes = failParts.join('; ');

  return { freshness, firmware, firmwareFront, battery, debugLog, raceLog, lifecycle, notes };
}

/** Firmware / lifecycle validation applies to rear units only, not paired front tags. */
export function isDeviceSubjectToFleetValidation(deviceId: string): boolean {
  return !isFrontDeviceId(deviceId);
}

export function getDeviceValidationMismatchReasons(
  cfg: RaceConfigurationRequirements | null | undefined,
  status: DeviceStatus | undefined,
  lifecycleStatusRaw?: unknown,
  nowMs?: number,
  /** Paired front device's status, when known — used to check firmwareFront. */
  frontStatus?: DeviceStatus,
): string[] {
  const b = getDeviceValidationBreakdown(cfg, status, lifecycleStatusRaw, nowMs, frontStatus);
  const reasons: string[] = [];
  if (b.freshness.result === 'fail' && b.freshness.detail) reasons.push(b.freshness.detail);
  for (const c of [b.firmware, b.firmwareFront, b.battery, b.debugLog, b.raceLog] as const) {
    if (c.result === 'fail' && c.detail) reasons.push(c.detail);
  }
  if (b.lifecycle.result === 'fail' && b.lifecycle.detail) reasons.push(b.lifecycle.detail);
  return reasons;
}
